import { Link, getRouteApi, useNavigate } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Trans, useTranslation } from "react-i18next";
import { AuthCard } from "@/components/AuthCard";
import { acceptInvitation, errorMessage, invitationPreviewQuery, meQuery } from "@/lib/api";

const route = getRouteApi("/invite/$token");

export function InvitePage() {
  const { token } = route.useParams();
  const { t } = useTranslation();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const preview = useQuery(invitationPreviewQuery(token));
  const me = useQuery(meQuery);
  const accept = useMutation({
    mutationFn: () => acceptInvitation(token),
    onSuccess: async (res) => {
      await qc.invalidateQueries({ queryKey: ["me"] });
      if (res.next_url) { location.href = res.next_url; return; }
      await navigate({ to: "/$ws/inbox", params: { ws: res.workspace_slug } });
    },
  });

  if (preview.isPending || me.isPending) {
    return <AuthCard title={t("common.loading")}>{null}</AuthCard>;
  }
  if (preview.isError || !preview.data) {
    return (
      <AuthCard title={t("invite.title")}>
        <p className="text-[13px] text-danger">{t("invite.invalid")}</p>
        <Link to="/" className="btn mt-6">
          {t("common.back")}
        </Link>
      </AuthCard>
    );
  }

  const inv = preview.data;
  const user = me.data?.user;
  const loginHref = `/login?email=${encodeURIComponent(inv.email)}&redirect=${encodeURIComponent(`/invite/${token}`)}`;

  return (
    <AuthCard title={t("invite.title")}>
      <p className="text-[13px] text-muted">
        <Trans i18nKey="invite.body" values={{ inviter: inv.inviter_name, workspace: inv.workspace_name }} components={{ 1: <span className="font-medium text-fg" /> }} />
      </p>
      <div className="mt-6 flex flex-col gap-3">
        {!user && (
          <>
            <p className="text-[12px] text-muted">{t("invite.signInAs", { email: inv.email })}</p>
            <a className="btn btn-primary h-9 justify-center" href={loginHref}>
              {t("auth.sendLink")}
            </a>
          </>
        )}
        {user && user.email !== inv.email && (
          <p className="text-[12px] text-warning">{t("invite.wrongAccount", { email: inv.email, current: user.email })}</p>
        )}
        {user && user.email === inv.email && (
          <button className="btn btn-primary h-9 justify-center" onClick={() => accept.mutate()} disabled={accept.isPending}>
            {accept.isPending ? t("invite.accepting") : t("invite.accept")}
          </button>
        )}
        {accept.isError && <p className="text-[12px] text-danger">{errorMessage(accept.error)}</p>}
      </div>
    </AuthCard>
  );
}
